import { io } from 'socket.io-client';
import { ORDER_SOCKET_URL } from '../constants/endpoints';
import { normalizeSymbolCodes } from '../utils/marketThumb';

const NAMESPACE = '/market';

let socket = null;
let subscriberCount = 0;
const codeRefCounts = new Map();

function getActiveCodes() {
  return [...codeRefCounts.keys()];
}

function emitSubscribe(sock, codes) {
  const normalized = normalizeSymbolCodes(codes);
  if (normalized.length > 0 && sock.connected) {
    sock.emit('subscribeThumb', { codes: normalized });
  }
}

function resubscribeAll(sock) {
  emitSubscribe(sock, getActiveCodes());
}

function getSocket() {
  if (!socket) {
    socket = io(`${ORDER_SOCKET_URL}${NAMESPACE}`, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
    });
    socket.on('connect', () => {
      resubscribeAll(socket);
    });
  }
  return socket;
}

function parsePayload(raw) {
  try {
    const body = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!body || body.code !== 200 || !body.data) {
      return null;
    }
    return body.data;
  } catch {
    return null;
  }
}

/**
 * 訂閱行情 thumb；回傳取消訂閱函式。
 * 僅訂閱傳入的 codes（頁面顯示哪些 symbol 就訂閱哪些）。
 */
export function subscribeMarketThumbs(codes, { onSnapshot, onUpdate } = {}) {
  const normalized = normalizeSymbolCodes(codes);
  // order 服務停用（ORDER_SOCKET_URL 為空）：不建立連線、不重連
  if (!ORDER_SOCKET_URL || normalized.length === 0) {
    return () => {};
  }

  const sock = getSocket();
  subscriberCount += 1;

  const onSnapshotEvent = (raw) => {
    const data = parsePayload(raw);
    if (data?.pushType === 'SNAPSHOT' && Array.isArray(data.thumbs)) {
      onSnapshot?.(data.thumbs);
    }
  };

  const onUpdateEvent = (raw) => {
    const data = parsePayload(raw);
    if (data?.pushType === 'UPDATE' && data.thumb) {
      onUpdate?.(data.thumb);
    }
  };

  sock.on('thumbSnapshot', onSnapshotEvent);
  sock.on('thumbUpdate', onUpdateEvent);

  const codesToSubscribe = normalized.filter((code) => {
    const count = codeRefCounts.get(code) || 0;
    codeRefCounts.set(code, count + 1);
    return count === 0;
  });

  if (sock.connected) {
    emitSubscribe(sock, codesToSubscribe);
  }

  return () => {
    sock.off('thumbSnapshot', onSnapshotEvent);
    sock.off('thumbUpdate', onUpdateEvent);
    subscriberCount -= 1;

    const codesToUnsubscribe = normalized.filter((code) => {
      const count = codeRefCounts.get(code) || 0;
      if (count <= 1) {
        codeRefCounts.delete(code);
        return true;
      }
      codeRefCounts.set(code, count - 1);
      return false;
    });

    if (codesToUnsubscribe.length > 0 && sock.connected) {
      sock.emit('unsubscribeThumb', { codes: codesToUnsubscribe });
    }

    if (subscriberCount <= 0 && socket) {
      socket.disconnect();
      socket = null;
      subscriberCount = 0;
      codeRefCounts.clear();
    }
  };
}
